import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Archive, Trash2, Eye, ArrowLeft, RotateCcw, Search, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArchivedOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const filteredOrders = orders.filter(order => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return [order.client_name, order.order_number, order.order_type, order.cemetery, order.stone_type, order.stone_colour]
      .some(v => v && String(v).toLowerCase().includes(q));
  });

  useEffect(() => {
    fetchArchivedOrders();
  }, []);

  const fetchArchivedOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/orders/archived/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load archived orders');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (orderId, orderNumber) => {
    if (!window.confirm(`Restore order #${orderNumber} to active orders?`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/api/orders/${orderId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order #${orderNumber} restored to active orders`);
      fetchArchivedOrders();
    } catch (error) {
      let errorMessage = 'Failed to unarchive order';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleClearArchived = async () => {
    if (!window.confirm('Are you sure you want to permanently delete all archived orders? This action cannot be undone!')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/orders/archived/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Archived orders cleared successfully');
      fetchArchivedOrders();
    } catch (error) {
      toast.error('Failed to clear archived orders');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Loading archived orders...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Back Button */}
      <Button 
        variant="outline" 
        onClick={() => navigate('/')} 
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>
      
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Archive className="w-6 h-6" />
                Archived Orders
              </CardTitle>
              <p className="text-sm text-slate-500 mt-2">View and manage completed archived orders</p>
            </div>
            <div className="flex gap-2 items-center">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, order #, cemetery…"
                  className="pl-9 pr-8 w-64"
                  data-testid="archived-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="archived-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {orders.length > 0 && (
                <Button onClick={handleClearArchived} variant="destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Archived
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Archive className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>{searchQuery ? `No archived orders match "${searchQuery}"` : 'No archived orders found'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map(order => (
                <div 
                  key={order.id} 
                  className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold">{order.client_name}</h3>
                        <Badge className="bg-slate-500">Order #{order.order_number}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 capitalize mb-1">{order.order_type}</p>
                      <p className="text-sm text-slate-500">Cemetery: {order.cemetery}</p>
                      <p className="text-sm text-slate-500">
                        Archived: {new Date(order.archived_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Badge className="bg-green-600">Completed</Badge>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/orders/${order.id}`)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleUnarchive(order.id, order.order_number)}
                          data-testid={`unarchive-order-${order.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
